import React, { useEffect, useState } from "react";
import { Card, Modal } from "reactstrap";

import AppSettingsService from "../../../services/appSettings";
import { ROLE_LABELS } from "../../../constants/roles";
import { toUkDisplayDate } from "../../../helpers/ukDate";

const label = role => ROLE_LABELS[role] || `Role ${role}`;

/** UK time of day, e.g. 14:05 - the date alone does not say which of two same-day changes came first. */
const ukTime = (value) => {
    try {
        return new Date(value).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
    } catch (e) {
        return "";
    }
};

/**
 * Every role change for one clinician: who changed it, from what, to what, when, and whether their
 * sessions were ended. superAdmin only.
 */
export default function RoleHistoryModal({ clinician, onClose }) {
    const [changes, setChanges] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const { data } = await AppSettingsService.getClinicianRoleHistory(clinician.id);
                if (!cancelled) {
                    setChanges(data || []);
                }
            } catch (e) {
                if (!cancelled) {
                    setError("Could not load the role history.");
                }
            }
        })();

        return () => { cancelled = true; };
    }, [clinician.id]);

    return (
        <Modal isOpen className="modal-size" centered>
            <Card className="p-4 mb-0" data-testid="role-history">
                <header className="font-size-22">
                    {`Role history - ${clinician.firstName} ${clinician.lastName}`}
                </header>

                <div className="mt-4">
                    {error ? <p className="text-danger" role="alert">{error}</p> : null}
                    {!error && changes === null ? <p className="text-muted">Loading…</p> : null}
                    {changes && !changes.length ? (
                        <p className="text-muted">No role changes recorded.</p>
                    ) : null}
                    {changes && changes.length ? (
                        <ul className="list-unstyled mb-0">
                            {changes.map((change, index) => (
                                <li key={index} className="mb-3" data-testid="role-change">
                                    <div>
                                        <strong>{`${label(change.fromRole)} → ${label(change.toRole)}`}</strong>
                                    </div>
                                    <div className="text-muted font-size-12">
                                        {`${toUkDisplayDate(change.changedAt)} ${ukTime(change.changedAt)}`
                                            + `${change.changedBy ? ` by ${change.changedBy.name}` : ""}`}
                                        {change.sessionsEndedAt
                                            ? " · signed out everywhere"
                                            : " · sign-out NOT confirmed"}
                                        {change.fullPatientAccessCleared ? " · all-patients access withdrawn" : ""}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>

                <button className="btn btn-outline-primary w-100 mt-4" onClick={onClose}>Close</button>
            </Card>
        </Modal>
    );
}
